/**
 * View Food History Page - Display detailed view of a specific food history record
 * Mapped to backend FoodHistoryDTO with exact field names
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Calendar } from 'lucide-react';
import { foodHistoryApi, type FoodHistory } from '../../services/gaushala/api';

export default function ViewFoodHistory() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [record, setRecord] = useState<FoodHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecord = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const result = await foodHistoryApi.getFoodHistoryById(parseInt(id));

        console.log('Food History API Result:', result);

        if (result.success && result.data) {
          console.log('Food History Record from API:', result.data);
          setRecord(result.data);
        } else {
          setError(result.error || 'Food history record not found');
        }
      } catch (error) {
        console.error('Error fetching food history record:', error);
        setError('Failed to load food history details');
      } finally {
        setLoading(false);
      }
    };

    fetchRecord();
  }, [id]);

  const handleEdit = () => {
    navigate(`/gaushala/food-history/edit/${id}`);
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this food history record?')) {
      return;
    }

    try {
      const result = await foodHistoryApi.deleteFoodHistory(parseInt(id));

      if (result.success) {
        navigate('/gaushala/food-history');
      } else {
        alert(result.error || 'Failed to delete food history record');
      }
    } catch (error) {
      console.error('Error deleting food history record:', error);
      alert('Failed to delete food history record');
    }
  };

  const handleBack = () => {
    navigate('/gaushala/food-history');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatFeedingDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Loading food history...</div>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Food History Not Found</h1>
            <p className="text-gray-600 mt-1">{error || 'The requested food history record could not be found.'}</p>
          </div>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Food History Record #{record.id}</h1>
          <p className="text-gray-600 mt-1">{formatDate(record.date)}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="pb-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">Shed Number</p>
              <p className="text-gray-900 mt-1">{record.shed?.shedNumber || `Shed #${record.shedId}` || '-'}</p>
            </div>

            <div className="pb-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">Shed Name</p>
              <p className="text-gray-900 mt-1">{record.shed?.shedName || '-'}</p>
            </div>

            <div className="pb-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">Gaushala</p>
              <p className="text-gray-900 mt-1">{record.gaushala?.gaushalaName || `Gaushala #${record.gaushalaId}` || '-'}</p>
            </div>

            {/* Feed Information */}
            <div className="pb-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">Feed Item</p>
              <p className="text-gray-900 mt-1">{record.inventory?.itemName || `Item #${record.inventoryId}` || '-'}</p>
            </div>

            <div className="pb-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">Feed Type</p>
              <p className="text-gray-900 mt-1">{record.inventory?.typeName || '-'}</p>
            </div>

            {/* Consumption Details */}
            <div className="pb-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">Consume Quantity (kg)</p>
              <p className="text-gray-900 mt-1">{record.consumeQuantity?.toFixed(1) || '0.0'}</p>
            </div>

            <div className="pb-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">Duration</p>
              <p className="text-gray-900 mt-1">{record.duration || '-'}</p>
            </div>

            <div className="pb-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">Date</p>
              <p className="text-gray-900 mt-1">{record.date ? formatDate(record.date) : '-'}</p>
            </div>

            <div className="pb-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">Created At</p>
              <p className="text-gray-900 mt-1">{record.createdAt ? formatDate(record.createdAt) : '-'}</p>
            </div>

            <div className="pb-3">
              <p className="text-sm font-medium text-gray-700">Last Updated</p>
              <p className="text-gray-900 mt-1">{record.updatedAt ? formatDate(record.updatedAt) : '-'}</p>
            </div>

            {/* Comments */}
            <div className="md:col-span-2">
              <p className="text-sm font-medium text-gray-700">Comments & Notes</p>
              {record.comments ? (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mt-1">
                  <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{record.comments}</p>
                </div>
              ) : (
                <p className="text-gray-900 mt-1">-</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleEdit}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Edit className="h-4 w-4" />
          Edit Record
        </button>
        <button
          onClick={handleBack}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to List
        </button>
      </div>
    </div>
  );
}
